<script setup lang="ts">
import { PracticeRecordingType } from 'types/types'
import { computed, onMounted, reactive, ref, toRefs } from 'vue'
import { useLogger } from 'vue-logger-plugin'

import EmptyStateCard from '@/components/EmptyStateCard.vue'
import PlaybackOverlay from '@/components/PlaybackOverlay.vue'
import { formattedDate, practiceRecordingDescription } from '@/composables/format'
import { useUtil } from '@/composables/util'
import store from '@/store'

const logger = useLogger()
const { loginCheck } = useUtil(logger)

const overlayRef = ref<InstanceType<typeof PlaybackOverlay>>()
const selected = ref<string[]>([])

interface MyRecordingsState {
  editingNotesId: string | null
  editingNotesText: string
}

const state = reactive<MyRecordingsState>({
  editingNotesId: null,
  editingNotesText: '',
})

const { editingNotesId, editingNotesText } = toRefs(state)

const recordings = computed(() => store.getters.practiceRecordings.recordings)
const loading = computed(() => store.getters.practiceRecordings.loading)

// {{{ notes editing
function startEditNotes(recording: PracticeRecordingType) {
  state.editingNotesId = recording.id
  state.editingNotesText = recording.notes ?? ''
}

async function saveNotes() {
  if (!state.editingNotesId) {
    return
  }
  const result = await store.dispatch.practiceRecordings.updateRecording({
    id: state.editingNotesId,
    notes: state.editingNotesText,
  })
  if (!result) {
    return // keep the editor open so the notes aren't lost
  }
  store.dispatch.snackbar.add({ message: 'Notes saved', state: 'primary' })
  cancelEditNotes()
}

function cancelEditNotes() {
  state.editingNotesId = null
  state.editingNotesText = ''
}
// }}}

// The browser flips a switch the moment it is clicked. When saving fails the recording is unchanged, so
// Vue sees nothing to update; changing the key remounts the switches showing what the server still has.
const sharedSwitchesKey = ref(0)

async function toggleShared(recording: PracticeRecordingType) {
  const newShared = !recording.shared
  const result = await store.dispatch.practiceRecordings.updateRecording({
    id: recording.id,
    shared: newShared,
  })
  if (result) {
    const msg = newShared ? 'Recording shared with teacher' : 'Recording unshared'
    store.dispatch.snackbar.add({ message: msg, state: 'primary' })
  } else {
    sharedSwitchesKey.value++
  }
}

function deleteRecording(recording: PracticeRecordingType) {
  store.dispatch.dialog.show({
    acceptColor: 'error',
    acceptText: 'Delete',
    isRejectable: true,
    message: 'Delete this recording? This cannot be undone.',
    onAccept: async () => {
      const result = await store.dispatch.practiceRecordings.deleteRecording(recording.id)
      if (result) {
        store.dispatch.snackbar.add({ message: 'Recording deleted', state: 'primary' })
      }
    },
    title: 'Delete Recording',
  })
}

function deleteSelected() {
  const count = selected.value.length
  store.dispatch.dialog.show({
    acceptColor: 'error',
    acceptText: `Delete ${count}`,
    isRejectable: true,
    message: `Delete ${count} recording${count === 1 ? '' : 's'}? This cannot be undone.`,
    onAccept: async () => {
      const result = await store.dispatch.practiceRecordings.deleteRecordings(selected.value)
      if (result) {
        store.dispatch.snackbar.add({
          message: `${result.deletedCount} recording${result.deletedCount === 1 ? '' : 's'} deleted`,
          state: 'primary',
        })
        selected.value = []
      }
    },
    title: 'Delete Recordings',
  })
}

const headers = [
  { key: 'created', sortable: true, title: 'Date' },
  { key: 'description', sortable: false, title: 'What I Practiced' },
  { key: 'notes', sortable: false, title: 'Notes' },
  { key: 'shared', sortable: true, title: 'Shared' },
  { key: 'actions', sortable: false, title: 'Actions' },
]

onMounted(() => {
  loginCheck()
  store.dispatch.practiceRecordings.fetchMyRecordings()
})
</script>

<template>
  <v-container>
    <h2 class="text-h5 mb-4">Recordings</h2>

    <v-progress-linear v-if="loading" color="primary" indeterminate />

    <v-data-table
      v-if="recordings.length"
      v-model="selected"
      density="comfortable"
      :headers="headers"
      item-value="id"
      :items="recordings"
      :items-per-page="25"
      :show-select="recordings.length > 1"
    >
      <template #header.data-table-select="{ allSelected, selectAll, someSelected }">
        <v-checkbox-btn
          :indeterminate="someSelected && !allSelected"
          :model-value="allSelected"
          @update:model-value="selectAll(!allSelected)"
        />
        <template v-if="selected.length">
          <span class="text-body-2 text-no-wrap mr-2">{{ selected.length }} selected</span>
          <v-btn color="error" icon="mdi-delete" size="small" variant="text" @click="deleteSelected" />
        </template>
      </template>

      <template #item.created="{ item }">
        {{ formattedDate(item.created) }}
      </template>

      <template #item.description="{ item }">
        {{ practiceRecordingDescription(item) }}
      </template>

      <template #item.notes="{ item }">
        <template v-if="editingNotesId === item.id">
          <v-text-field
            v-model="editingNotesText"
            density="compact"
            hide-details
            variant="outlined"
            @keyup.enter="saveNotes"
            @keyup.escape="cancelEditNotes"
          />
          <div class="mt-1">
            <v-btn color="primary" size="x-small" variant="text" @click="saveNotes">Save</v-btn>
            <v-btn size="x-small" variant="text" @click="cancelEditNotes">Cancel</v-btn>
          </div>
        </template>
        <template v-else>
          <span class="text-body-2">{{ item.notes || '—' }}</span>
          <v-btn class="ml-2" density="compact" icon="mdi-pencil" size="x-small" variant="text" @click="startEditNotes(item)" />
        </template>
      </template>

      <template #item.shared="{ item }">
        <v-switch
          :key="sharedSwitchesKey"
          color="primary"
          density="compact"
          hide-details
          :model-value="item.shared"
          @update:model-value="toggleShared(item)"
        />
      </template>

      <template #item.actions="{ item }">
        <v-btn color="primary" icon="mdi-play" variant="text" @click="overlayRef?.openOverlay(item)" />
        <v-btn color="primary" icon="mdi-download" variant="text" @click="overlayRef?.downloadRecording(item)" />
        <v-btn color="error" icon="mdi-delete" variant="text" @click="deleteRecording(item)" />
      </template>
    </v-data-table>

    <empty-state-card
      v-else-if="!loading"
      description='Start practicing with the "Record me" toggle on the Practice page!'
      icon="mdi-microphone-off"
      title="No recordings yet"
    />

    <playback-overlay ref="overlayRef" :bar-mitzvah="store.getters.student.barMitzvah" />
  </v-container>
</template>
